import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import authHeader from "../services/auth-header";
import { API_BASE_URL } from "../common/config";
import { categoryIDtoLabel, getCategoryValue, getRiskSeverity, getScoreBorderClass } from "../common/helpers";
import LoadPage from "../common/LoadPage";
import useAxiosFetch from "../common/useAxiosFetch";
import { updateDb } from "../services/updateDb";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../AppContext";
import AlertMessage from "../common/AlertMessage";
import useSoaCheck from "../common/useSoaCheck";
import InfoTooltip from '../common/InfoTooltip';
import { MODAL_TYPES } from '../modals/modalTypes';

const MeasuresPage = () => {
  const { t, i18n } = useTranslation();
  const { currentOrg, currentProject, showAlertMessage, alertMessage, openModal, modalState } = useAppContext();
  const location = useLocation();
  const { data, loading, refetch } = useAxiosFetch("/api/risks/" + (currentProject.id ? "byProject/" + currentProject.id : "byOrg/" + currentOrg.id));
  const { checkSoaAfterMeasureUpdate } = useSoaCheck();
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [deadlineDateFilter, setDeadlineDateFilter] = useState('');
  const [ownerFilter , setOwnerFilter] = useState('');
  const [sortMode, setSortMode] = useState('risk');
  const [adviceData, setAdviceData] = useState(null);
  const abortController = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cat = params.get('categoryFilter');
    if (cat) setCategoryFilter(cat);
  }, [location.search]);

  const getAdvice = async (riskId, measureIndex) => {

      if (abortController.current) { 
          abortController.current.abort();
      }
      abortController.current = new AbortController();

      try {
          const response = await fetch(`${API_BASE_URL}/api/risks/advice/measures/${riskId}/${measureIndex}/${i18n.language}?standard=${encodeURIComponent(currentOrg.securityStandard || 'ISO27001')}`, { 
              headers: authHeader(), 
              signal: abortController.current.signal 
          });

          // Check if request was blocked by backend
          if (response.status === 403) {
              const errorData = await response.json();
              if (errorData.upgradeRequired) {
                  openModal(MODAL_TYPES.UPGRADE, {
                      featureType: 'AI',
                      currentLimit: errorData.limit,
                      currentCount: errorData.current,
                      recommendedPlan: 'premium'
                  });
                  return;
              }
          }

          const reader = response.body.getReader();
          let advice = '';
          while (true) {
              const { done, value } = await reader.read();
              if (done) {
                  advice += `\n\n*${t('disclaimer_txt')}*`;
                  setAdviceData(advice);
                  break;
              }
              advice += new TextDecoder("utf-8").decode(value);
              setAdviceData(advice);
          }
      } catch (error) {
          if (error.name === 'AbortError') {
              console.log('Fetch aborted');
          } else {
              console.error('Failed to get advice:', error);
          }
      }
  };

  // Cleanup effect to abort any ongoing fetch when the modal is closed or type changes
  useEffect(() => {
    if(!modalState.show || modalState.type !== MODAL_TYPES.INFO)  {
      if (abortController.current) { 
        abortController.current.abort();
      }
    }
  }, [modalState.show, modalState.type]);

  useEffect(() => {
    if (adviceData) {
      openModal(MODAL_TYPES.INFO, { content: adviceData, title: 'CoPilot ' + t('advice'), isMarkdown: true });
    }
  }, [adviceData, openModal, t]);

  const navigate = useNavigate();

  const navigateRiskDetails = (riskId) => {
    navigate(`/viewrisk?riskId=${riskId}`);
  };

  const openCoPilot = (riskId, measureIndex) => {
    setAdviceData(null);
    openModal(MODAL_TYPES.INFO, { content: 'loading', title: 'CoPilot ' + t('advice') });
    getAdvice(riskId, measureIndex);
  };

  const openMeasureModal = (measure) => {
    openModal(MODAL_TYPES.MEASURE, {
      measureObj: measure,
      handleClose: handleClose,
      uniqueOwners: uniqueOwners
    });
  };

  const handleClose = async (updateRisk, riskId, measureObj) => {
    // Close the modal using the modal system
    openModal(null);

    if(updateRisk && riskId && measureObj) {
      // keep version of old risk object
      const riskObj = data.find(risk => risk.id === riskId);
      const res = await updateDb("/api/risks/update-measure/" + riskId, measureObj);

      if (res.success) {
        showAlertMessage('warning', t('measure_update'));
        refetch();

        // fetch update risk object from api
        const updatedRiskObj = await fetch(`${API_BASE_URL}/api/risks/${riskId}`, { headers: authHeader() });
        const updatedRisk = await updatedRiskObj.json();
        
        // display delta in risk status between original riskObj and updateRisk object for the .measures
        if (riskObj.measures[measureObj.index].status !== measureObj.status) {
          openModal(MODAL_TYPES.UPDATE_RISK_SCORE, {
            riskObj: updatedRisk,
            updateReason: `- Measure update: ${measureObj.measure} from ${riskObj.measures[measureObj.index].status} -> ${measureObj.status}.\n`
          });
        } else {
          // Only check SoA here if UpdateRiskScoreModal was not opened;
          // otherwise the SoA check runs when that modal closes.
          const affectedCategories = (measureObj.category || []).map(c => getCategoryValue(c));
          if (affectedCategories.length > 0) {
            checkSoaAfterMeasureUpdate(affectedCategories);
          }
        }
      }
    }
  };
  
  const UNCATEGORIZED_KEY = '_uncategorized';
  const NO_OWNER_KEY = '_no_owner';

  const groupByCategory = useMemo(() => {
    let grouped = {};
  
    if (data && data.length > 0) {
  
      data.forEach(item => {
        let riskScore = (item['actual-chance'] || 0) * (item['actual-impact'] || 0),
          severity = getRiskSeverity(riskScore, currentOrg.rangeBounds),
          riskCategory = severity.charAt(0).toUpperCase() + severity.slice(1);
  
        item.measures?.filter(measure => 
          (!statusFilter || measure.status === statusFilter || (statusFilter === 'open' && (measure.status === 'todo' || measure.status === 'in_progress'))) &&
          (!categoryFilter || categoryFilter === UNCATEGORIZED_KEY
            ? (categoryFilter === UNCATEGORIZED_KEY ? (!measure.category || measure.category.length === 0) : true)
            : (measure.category && measure.category.some(cat => getCategoryValue(cat) === categoryFilter))) &&
          (!deadlineDateFilter || (
            measure.deadlineDate && (
              deadlineDateFilter === 'expired' && moment(measure.deadlineDate).isSameOrBefore(moment(), 'day') ||
              deadlineDateFilter === 'next_week' && moment(measure.deadlineDate).isBetween(moment(), moment().add(7, 'days'), 'day', '[]') ||
              deadlineDateFilter === 'next_month' && moment(measure.deadlineDate).isBetween(moment(), moment().add(30, 'days'), 'day', '[]') ||
              deadlineDateFilter === 'next_quarter' && moment(measure.deadlineDate).isBetween(moment(), moment().add(91, 'days'), 'day', '[]')
            )
          )) &&
          (!ownerFilter || (ownerFilter === NO_OWNER_KEY ? !measure.owner : measure.owner === ownerFilter))
        )
        .forEach(measure => {
          const enrichedMeasure = {
            ...measure,
            measureIndex: item.measures.indexOf(measure),
            riskId: item["id"],
            riskScore: (parseInt(item["actual-chance"]) || 0) * (parseInt(item["actual-impact"]) || 0),
            riskDescription: item.description
          };

          const categories = (measure.category || [])
            .filter(cat => !categoryFilter || categoryFilter === UNCATEGORIZED_KEY || getCategoryValue(cat) === categoryFilter);

          if (categories.length === 0) {
            // Measure has no categories assigned — collect in uncategorized group
            if (!grouped[UNCATEGORIZED_KEY]) {
              grouped[UNCATEGORIZED_KEY] = {
                  measures: [],
                  highRisks: 0,
                  mediumRisks: 0,
                  lowRisks: 0
              };
            }

            if (riskCategory === 'High') grouped[UNCATEGORIZED_KEY].highRisks++;
            else if (riskCategory === 'Medium') grouped[UNCATEGORIZED_KEY].mediumRisks++;
            else grouped[UNCATEGORIZED_KEY].lowRisks++;

            grouped[UNCATEGORIZED_KEY].measures.push(enrichedMeasure);
          } else {
            categories.forEach(cat => {
              let categoryValue = getCategoryValue(cat);
    
              if (!grouped[categoryValue]) {
                grouped[categoryValue] = {
                    measures: [],
                    highRisks: 0,
                    mediumRisks: 0,
                    lowRisks: 0
                };
              }
    
              if (riskCategory === 'High') grouped[categoryValue].highRisks++;
              else if (riskCategory === 'Medium') grouped[categoryValue].mediumRisks++;
              else grouped[categoryValue].lowRisks++;
    
              grouped[categoryValue].measures.push(enrichedMeasure);
            });
          }
        });
      });
    }
  
    return grouped;
  }, [data, statusFilter, categoryFilter, deadlineDateFilter, ownerFilter]);

  // Compute unique owners from both risk owners and measure owners
  const uniqueOwners = useMemo(() => {
      const riskOwners = data?.map(item => item.owner) || [];
      const measureOwners = data?.flatMap(item =>
        item.measures?.map(measure => measure.owner)
      ) || [];
      return Array.from(new Set([...riskOwners, ...measureOwners]))
        .filter(Boolean)
        .sort()
        .map(owner => ({ owner }));
  }, [data]);

  if (loading)
    return <LoadPage />;

  const sortedCategoryEntries = Object.entries(groupByCategory)
    .filter(([key]) => key !== UNCATEGORIZED_KEY)
    .sort((a, b) => {
    if (sortMode === 'category') {
      const aParts = a[0].split('.').map(Number);
      const bParts = b[0].split('.').map(Number);
      for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
        if ((aParts[i] || 0) < (bParts[i] || 0)) return -1;
        if ((aParts[i] || 0) > (bParts[i] || 0)) return 1;
      }
      return 0;
    }
    // De-structure to get highRisks, mediumRisks, and lowRisks values from both entries
    const [, { highRisks: highA, mediumRisks: mediumA, lowRisks: lowA }] = a;
    const [, { highRisks: highB, mediumRisks: mediumB, lowRisks: lowB }] = b;

    // Compare highRisks, then mediumRisks, and then lowRisks
    if (highA !== highB) return highB - highA;
    if (mediumA !== mediumB) return mediumB - mediumA;
    return lowB - lowA;
  });

  // Append uncategorized group at the end if it exists
  const uncategorizedGroup = groupByCategory[UNCATEGORIZED_KEY];
  if (uncategorizedGroup) {
    sortedCategoryEntries.push([UNCATEGORIZED_KEY, uncategorizedGroup]);
  }

  const TableRow = ({ category, measures, highRisks, mediumRisks, lowRisks }) => {
    return (
        <>
            {measures.map((measure, mIndex) => (
                <tr key={mIndex}>
                    {mIndex === 0 ? (
                        <>
                            <td className="align-left" rowSpan={measures.length}>
                                {category === UNCATEGORIZED_KEY 
                                  ? <em>{t('no_category')}</em>
                                  : categoryIDtoLabel(category, i18n.language, currentOrg.securityStandard)}
                            </td>
                            <td className="align-left" rowSpan={measures.length}>
                                <div className="score-border-red measure-score">{highRisks}</div>
                                <div className="score-border-yellow measure-score">{mediumRisks}</div> 
                                <div className="score-border-green measure-score">{lowRisks}</div> 
                            </td>
                        </>
                    ) : null}
                    <td className="align-left">{measure.measure}</td>
                    <td className="align-left">
                        {t((measure.status || ""))}
                    </td>
                    <td className="align-left">{measure.owner}</td>
                    <td className="align-left">{measure.deadlineDate ? moment(measure.deadlineDate).format("DD-MM-YYYY") : ""}</td>
                    <td className="align-left">
                        {measure.action && measure.action.startsWith("http") ? 
                            <a href={measure.action} className="action-link" target="_blank" rel="noopener noreferrer">{t('go_to_action')}</a> :
                            measure.action
                        }
                    </td>
                    <td>
                    <button
                        type="button"
                        onClick={() => openCoPilot(measure.riskId, measure.measureIndex)}
                        className="measure-ai-btn"
                        aria-label="CoPilot"
                      >
                        <i className="icon-stars1" />
                      </button>
                    </td>
                    <td>

                    <InfoTooltip content={<><b>{t('associated_risk')}:</b><br/>{measure.riskDescription}</>}>
                      <div 
                        onClick={() => navigateRiskDetails(measure.riskId)} 
                        className={"pointer-class " + getScoreBorderClass(measure.riskScore, currentOrg.rangeBounds)}
                      >{measure.riskScore}</div> 
                    </InfoTooltip>


                    {/* <i
                        
                        style={{  textAlign: 'center', margin: 0, fontSize: 22}}
                        className="icon-Risico pointer-class"
                      /> */}
                    </td>

                    <td><i
                        onClick={() => openMeasureModal(measure)}
                        style={{  textAlign: 'center', margin: 0}}
                        className="icon-edit"
                      /></td>
                </tr>
            ))}
        </>
    );
  };

  return (
    <div id="content">
      {/* <div id="content-title">Measures overview</div> */}

      <div className="content-header">

      <div className="white-border-btn" onClick={() => setSortMode(sortMode === 'risk' ? 'category' : 'risk')}><i className="icon-sort" /> {sortMode === 'risk' ? t('category') : t('risk_scores')} sort</div>

      <select className="blue-dropdown" style={{width: 200}} onChange={(e) => setCategoryFilter(e.target.value)} value={categoryFilter}>
          <option value="">{t('category')} filter</option>
          {
            // Check if there are any measures without categories
            data.some(item => item.measures?.some(measure => !measure.category || measure.category.length === 0))
              && <option value={UNCATEGORIZED_KEY}>{t('no_category')}</option>
          }
          {
            // Generate a set of unique categories across all measures
            Array.from(new Set(
              data.flatMap(item => 
                item.measures?.flatMap(measure => 
                  measure.category 
                    ? measure.category.map(cat => getCategoryValue(cat)) // Map each category to its value
                    : []
                )
              ))
            )
            .filter(Boolean) // Remove any empty string or falsy values
            .sort((a, b) => {
              const aParts = a.split('.').map(Number);
              const bParts = b.split('.').map(Number);
              for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
                if ((aParts[i] || 0) < (bParts[i] || 0)) return -1;
                if ((aParts[i] || 0) > (bParts[i] || 0)) return 1;
              }
              return 0;
            }) // Sort the category values using a custom numerical sort
            .map(cat => (
              <option key={cat} value={cat}>
                {categoryIDtoLabel(cat, i18n.language, currentOrg.securityStandard)} {/* assuming i18n.language is defined and categoryIDtoLabel is a functional translation method */}
              </option>
            ))
          }
        </select>

        <select className="blue-dropdown" style={{width: 150}} onChange={(e) => setStatusFilter(e.target.value)} value={statusFilter}>
            <option value="">Status filter</option>
            <option value="todo">{t('todo')}</option>
            <option value="in_progress">{t('in_progress')}</option>
            <option value="on_hold">{t('on_hold')}</option>
            <option value="implemented">{t('implemented')}</option>
            <option value="dismissed">{t('dismissed')}</option>
            <option value="open">Open ({t('todo')} + {t('in_progress')})</option>
        </select>

        <select className="blue-dropdown" onChange={(e) => setOwnerFilter(e.target.value)} value={ownerFilter}>
          <option value="">{t('owner_without_prefix')} filter</option>
          <option value={NO_OWNER_KEY}>{t('no_owner')}</option>
          {
            uniqueOwners.map(item => (
              <option key={item.owner} value={item.owner}>
                {item.owner}
              </option>
            ))
          }
        </select>                

        <select className="blue-dropdown" onChange={(e) => setDeadlineDateFilter(e.target.value)} value={deadlineDateFilter}>
            <option value="">Deadline {t('date').toLowerCase()} filter</option>
            <option value="expired">{t('expired')} deadlines</option>
            <option value="next_week">{t('next_week')}</option>
            <option value="next_month">{t('next_month')}</option>
            <option value="next_quarter">{t('next_quarter')}</option>
        </select>

        

      </div>

      <div className="clear-both" />

      <AlertMessage type={alertMessage.type} message={alertMessage.content} />

      <div className="content-bubble content-bubble-table">
        <div className="table-wrapper">
          <table>
              <thead className="sticky-header">
                  <tr>
                      <td className="align-left" style={{width: '20vw'}}>{t('category')}</td>
                      <td className="align-left">{t('risk_scores')} (H/M/L)</td>
                      <td className="align-left">{t('measure')}</td>
                      <td className="align-left">{t('status')}</td>
                      <td className="align-left">{t('owner_without_prefix')}</td>
                      <td className="align-left">Deadline {t('date').toLowerCase()}</td>
                      <td className="align-left">{t('link_to_actions')}</td>
                      <td className="align-left">A.I.</td>
                      <td className="align-left">{t('risk')}</td>
                      <td>&nbsp;</td>
                  </tr>
              </thead>
              <tbody>
              {sortedCategoryEntries.map(([category, { measures, highRisks, mediumRisks, lowRisks }]) => (
                  <TableRow key={category} category={category} measures={measures} highRisks={highRisks} mediumRisks={mediumRisks} lowRisks={lowRisks} />
              ))}

              </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MeasuresPage;